import { useQuery } from '@tanstack/react-query';
import { Navigate, useLocation } from 'react-router-dom';
import { AlertTriangle, ArrowRight, BarChart3, FlaskConical, LogIn } from 'lucide-react';
import { useAuthStore, type AuthOrg, type AuthUser } from './store';
import { beginLogin, isDevMode } from './oidc';

interface DevUser extends AuthUser {
  orgs: AuthOrg[];
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

function LoginShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-grey-50 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary shadow-elevated">
            <BarChart3 className="h-7 w-7 text-white" aria-hidden />
          </div>
          <h1 className="text-heading-1 text-secondary">
            Solid <span className="text-primary">Financials</span>
          </h1>
          <p className="mt-1 text-body-sm text-grey-500">
            Budgets, invoices, and forecasting for the Solid Digital Hub
          </p>
        </div>
        {children}
        <p className="mt-6 text-center text-caption text-grey-400">
          Part of the Solid Digital Hub &middot; soliddigital.com
        </p>
      </div>
    </div>
  );
}

function DevSignIn() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const {
    data: users,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery<DevUser[]>({
    queryKey: ['auth', 'dev-users'],
    queryFn: async () => {
      const response = await fetch('/api/auth/dev-users', {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Backend replied ${response.status} — is it running on :4010?`);
      return (await response.json()) as DevUser[];
    },
    staleTime: 60_000,
    retry: 1,
  });

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center gap-2 border-b border-grey-200 bg-warning/10 px-5 py-3">
        <FlaskConical className="h-4 w-4 shrink-0 text-warning" aria-hidden />
        <p className="text-body-sm font-semibold text-secondary">
          Development sign-in
          <span className="ml-2 font-normal text-grey-500">no identity provider required</span>
        </p>
      </div>

      {isLoading && (
        <div className="space-y-3 p-5">
          {[0, 1].map((i) => (
            <div key={i} className="flex animate-pulse items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-grey-200" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-1/3 rounded bg-grey-200" />
                <div className="h-3 w-1/2 rounded bg-grey-100" />
              </div>
            </div>
          ))}
        </div>
      )}

      {isError && (
        <div className="flex flex-col items-center gap-3 p-6 text-center">
          <AlertTriangle className="h-6 w-6 text-warning" aria-hidden />
          <p className="text-body-sm text-secondary-600">
            Could not load dev users. {error instanceof Error ? error.message : ''}
          </p>
          <button type="button" className="btn-outline btn-sm" onClick={() => void refetch()}>
            Try again
          </button>
        </div>
      )}

      {users && users.length === 0 && (
        <p className="p-6 text-center text-body-sm text-grey-500">
          No dev users found — add one to backend/src/lib/dev-users.json.
        </p>
      )}

      {users && users.length > 0 && (
        <ul className="divide-y divide-grey-100">
          {users.map((user) => (
            <li key={user.email}>
              <button
                type="button"
                onClick={() =>
                  setAuth({ accessToken: `dev:${user.email}`, refreshToken: null, user })
                }
                className="group flex w-full items-center gap-3 px-5 py-4 text-left transition-colors hover:bg-grey-50 focus:outline-none focus-visible:bg-grey-50"
              >
                <span className="avatar avatar-md bg-secondary text-white">
                  {initials(user.name)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold text-secondary">{user.name}</span>
                  <span className="block truncate text-body-sm text-grey-500">{user.email}</span>
                  <span className="mt-1 flex flex-wrap gap-1">
                    {user.orgs.map((org) => (
                      <span key={org.id} className="badge-secondary">
                        {org.name}
                        <span className="ml-1 text-grey-500">&middot; {org.role}</span>
                      </span>
                    ))}
                  </span>
                </span>
                <ArrowRight
                  className="h-4 w-4 shrink-0 text-grey-300 transition-all group-hover:translate-x-0.5 group-hover:text-secondary"
                  aria-hidden
                />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OidcSignIn({ returnTo }: { returnTo: string }) {
  return (
    <div className="card p-8 text-center">
      <h2 className="text-heading-3 text-secondary">Sign in to continue</h2>
      <p className="mt-2 text-body-sm text-grey-500">
        Use your Solid Digital account. You&rsquo;ll be redirected to
        auth.soliddigital.com and brought right back.
      </p>
      <button
        type="button"
        className="btn-primary mt-6 w-full"
        onClick={() => void beginLogin(returnTo)}
      >
        <LogIn className="h-4 w-4" aria-hidden />
        Sign in with Solid ID
      </button>
    </div>
  );
}

export function LoginPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();
  const returnTo = (location.state as { from?: string } | null)?.from ?? '/dashboard';

  if (isAuthenticated) return <Navigate to={returnTo} replace />;

  return <LoginShell>{isDevMode() ? <DevSignIn /> : <OidcSignIn returnTo={returnTo} />}</LoginShell>;
}
